import { Modal, VStack } from '@musinsa/mwms-ds'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import Text from '@/components/content/Text'
import { colorSystem } from '@/styles/theme'
import { PrioritySelectors } from './PrioritySelectors'
import { OverseasPriorityForm, overseasPriorityFormSchema } from '../../schemas/priorityFormSchema'
import { useSuspenseGetOverseasPriority, useSetOverseasPriority } from '@/services/outboundSchedule2/useOutboundScheduleQueries'
import { SetOverseasPriorityReq } from '@/services/outboundSchedule2/types'
import { AxiosError } from 'axios'
import { Utils } from '@/utils/Utils'
import MAlert from '@/utils/modal/MAlert'
import MToast from '@/utils/modal/MToast'
import { useGetCutOffOverseasWarehouses } from '@/services/warehouse/useWareHouseQueries'
import { MENU_CODE } from '../../constants'

type Props = {
  onClose: () => void
}

export function OverseasPriorityModal({ onClose }: Props) {
  const { data: overseasPriority } = useSuspenseGetOverseasPriority(MENU_CODE)

  const {
    watch,
    setValue,
    getValues,
    formState: { isValid },
  } = useForm<OverseasPriorityForm>({
    resolver: zodResolver(overseasPriorityFormSchema),
    defaultValues: {
      warehouseCodes: overseasPriority?.priorities
        .filter((_, idx) => idx < 3)
        .map((p) => p.warehouseCode) as OverseasPriorityForm['warehouseCodes'],
    },
  })

  const { options: overseasWarehouseOptions } = useGetCutOffOverseasWarehouses()

  const setOverseasPriority = useSetOverseasPriority(MENU_CODE)

  const handleFormSubmit = async () => {
    const { warehouseCodes = [] } = getValues()

    const payload: SetOverseasPriorityReq = {
      priorities: (warehouseCodes.filter(Boolean) as string[])
        .filter((_, idx) => idx < 3)
        .map((code, idx) => ({
          warehouseCode: code,
          priority: idx + 1,
        })),
    }

    try {
      await setOverseasPriority.mutateAsync(payload)

      MToast.show('저장되었습니다.')
    } catch (e) {
      MAlert.show(Utils.getApiErrMessage(e as AxiosError<ApiResponseV2>, '해외 할당 우선순위 설정에 실패했습니다.'))
    }

    onClose()
  }

  return (
    <Modal
      open
      onClose={onClose}
      title="해외 출고 관리"
      buttons={[
        { kind: 'secondary', content: '취소', onClick: onClose },
        { kind: 'primary_light', content: '저장', onClick: handleFormSubmit, disabled: !isValid },
      ]}
    >
      <VStack gap={16}>
        <VStack gap={4}>
          <Text kind="Headline_16_bold">해외 할당 우선순위</Text>
          <Text kind="Body_12_regular" color={colorSystem.text.common.tertiary}>
            해외 출고는 컷오프 우선순위가 아닌 할당 우선순위를 따릅니다.
          </Text>
        </VStack>

        <PrioritySelectors
          warehouseOptions={overseasWarehouseOptions}
          type="OVERSEAS"
          warehouseCodes={watch('warehouseCodes')}
          onWarehouseChange={(idx, value) => setValue(`warehouseCodes.${idx}`, value, { shouldValidate: true })}
        />
      </VStack>
    </Modal>
  )
}
